import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Lightning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { redistributeObjectives } from '../lib/objectiveSliders';
import ClientWorkspaceRecovery from './ClientWorkspaceRecovery';

const DEFAULT_OBJECTIVES = { awareness: 60, education: 30, monetizing: 10 };

export default function ExistingClientQuickStart({ api, initialClientId = '', onClose, onOpenProject, onResumeWizard }) {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [workspace, setWorkspace] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceFailed, setWorkspaceFailed] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [publicationCount, setPublicationCount] = useState(7);
  const [awareness, setAwareness] = useState(DEFAULT_OBJECTIVES.awareness);
  const [education, setEducation] = useState(DEFAULT_OBJECTIVES.education);
  const [monetizing, setMonetizing] = useState(DEFAULT_OBJECTIVES.monetizing);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [proEditorialEngine, setProEditorialEngine] = useState(false);
  const [entitlementsResolved, setEntitlementsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/plan/entitlements')
      .then(({ data }) => {
        if (!cancelled) setProEditorialEngine(Boolean(data?.entitlements?.pro_editorial_engine));
      })
      .catch(() => {
        if (!cancelled) setProEditorialEngine(false);
      })
      .finally(() => {
        if (!cancelled) setEntitlementsResolved(true);
      });
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    api.get('/client-brains').then(response => {
      if (cancelled) return;
      const list = response.data || [];
      setClients(list);
      if (initialClientId && list.some(item => item.id === initialClientId)) {
        setSelectedId(initialClientId);
        setProjectName('');
      }
    }).catch(() => {
      if (!cancelled) toast.error('Impossibile caricare i clienti esistenti');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, initialClientId]);

  const selected = useMemo(() => clients.find(item => item.id === selectedId) || null, [clients, selectedId]);

  useEffect(() => {
    if (!selectedId) { setWorkspace(null); return; }
    let cancelled = false;
    setWorkspace(null);
    setWorkspaceFailed(false);
    setWorkspaceLoading(true);
    api.get(`/client-brains/${selectedId}/workspace`)
      .then(({ data }) => { if (!cancelled) setWorkspace(data); })
      .catch(() => { if (!cancelled) { setWorkspace(null); setWorkspaceFailed(true); } })
      .finally(() => { if (!cancelled) setWorkspaceLoading(false); });
    return () => { cancelled = true; };
  }, [api, selectedId]);

  const chooseClient = id => {
    setSelectedId(id);
    setProjectName('');
  };

  const handleObjectiveChange = (changedKey, rawValue) => {
    const next = redistributeObjectives({ awareness, education, monetizing }, changedKey, rawValue);
    setAwareness(next.awareness);
    setEducation(next.education);
    setMonetizing(next.monetizing);
  };

  const defaults = workspace?.workspace_defaults || {};
  const v3TemplateCount = Array.isArray(defaults.design_v3_templates) ? defaults.design_v3_templates.length : 0;
  const hasV3Design = v3TemplateCount > 0;
  const hasSupportDesign = Boolean(defaults.design_dna_v2 && Object.keys(defaults.design_dna_v2).length);
  const hasDesign = hasV3Design || hasSupportDesign;
  const hasTov = Boolean(defaults.tov && Object.keys(defaults.tov).length);
  const canFastGenerate = hasTov;

  const create = async () => {
    if (!selected || !projectName.trim() || creating || !entitlementsResolved || workspaceLoading || workspaceFailed || !workspace) return;
    setCreating(true);
    const tid = toast.loading(
      canFastGenerate
        ? (proEditorialEngine ? 'Creo il progetto e genero i Master…' : 'Creo il progetto e genero i contenuti…')
        : 'Creo il progetto…'
    );
    try {
      const { data: project } = await api.post('/projects', {
        name: projectName.trim(),
        sector: selected.sector || 'Generale',
        description: selected.description || '',
        language: selected.language || 'it',
        client_brain_id: selected.id,
        objective_awareness: awareness,
        objective_education: education,
        objective_monetizing: monetizing,
        formats: ['reel', 'carousel'],
        duration_weeks: Math.ceil(publicationCount / 7),
        requested_content_count: publicationCount,
        geo: '',
        brief_notes: '',
        custom_instructions: '',
        project_mode: proEditorialEngine ? 'master' : 'social',
      });

      const { data: inherited } = await api.post(`/projects/${project.id}/client-workspace/inherit`);
      try { await api.post('/personas/generate', { project_id: project.id }); } catch { /* non-blocking */ }

      if (!inherited.has_tov) {
        const { data: wizardState } = await api.get(`/projects/${project.id}/wizard-state`);
        toast.success('Progetto creato. Il profilo cliente non ha ancora un Tone of Voice completo.', { id: tid });
        onResumeWizard?.(wizardState);
        return;
      }

      if (proEditorialEngine) {
        await api.post('/pro/master-projects/generate', { project_id: project.id });
      } else {
        await api.post('/hooks/generate', { project_id: project.id });
        const { data: generated } = await api.post('/content/generate', { project_id: project.id });
        if (generated?.success === false || !(generated?.count > 0)) {
          throw new Error('La generazione non ha prodotto contenuti. Il progetto è stato creato e può essere ripreso dal Wizard.');
        }
      }

      const { data: freshProject } = await api.get(`/projects/${project.id}`);
      toast.success(proEditorialEngine ? 'Progetto Master creato dal profilo cliente' : 'Progetto creato dal profilo cliente', { id: tid });
      onOpenProject?.(freshProject);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = detail && typeof detail === 'object' ? detail.message : detail;
      toast.error(message || e.message || 'Creazione progetto non completata', { id: tid });
    } finally {
      setCreating(false);
    }
  };

  const objectiveRows = [
    { id: 'awareness', label: 'Awareness', value: awareness },
    { id: 'education', label: 'Feeding', value: education },
    { id: 'monetizing', label: 'Monetizing', value: monetizing },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(5px)' }} onClick={onClose} data-testid="existing-client-quickstart-overlay">
      <div className="w-full max-w-xl rounded-2xl border border-[var(--border-color)] p-5 md:p-6 max-h-[92vh] overflow-y-auto" style={{ background: 'var(--bg-primary)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2"><Lightning size={20} className="text-[var(--accent-purple)]" /><h2 className="text-xl font-bold">Nuovo progetto · cliente esistente</h2></div>
            <p className="text-sm text-[var(--text-muted)] mt-1">Riusa il profilo cliente salvato. Qui definisci solo il nuovo progetto e il suo obiettivo editoriale.</p>
          </div>
          <button className="btn-ghost p-1.5" onClick={onClose}><X size={18} /></button>
        </div>

        {loading ? <p className="text-sm text-[var(--text-muted)] py-6">Caricamento clienti…</p> : clients.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--border-color)] p-4 text-sm text-[var(--text-muted)]">Non ci sono ancora Client Brain riutilizzabili. Crea il primo progetto dal Wizard completo.</div>
        ) : <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)] mb-2">Cliente</label>
            <select className="input-dark w-full" value={selectedId} onChange={e => chooseClient(e.target.value)} data-testid="quickstart-client-select">
              <option value="">Seleziona cliente…</option>
              {clients.map(client => <option key={client.id} value={client.id}>{client.name}{client.sector ? ` · ${client.sector}` : ''}</option>)}
            </select>
          </div>

          {selected && <>
            <div>
              <label className="block text-xs font-semibold uppercase text-[var(--text-muted)] mb-2">Nome progetto / campagna</label>
              <input className="input-dark w-full" value={projectName} onChange={e=>setProjectName(e.target.value)} placeholder="Es. Lancio autunno, Campagna Q4…" data-testid="quickstart-project-name" />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-[var(--text-muted)] mb-2">Pubblicazioni da pianificare</label>
              <input className="input-dark w-full" type="number" min="1" max="30" step="1"
                value={publicationCount} onChange={e => setPublicationCount(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                data-testid="quickstart-publication-count" />
              <p className="text-xs text-[var(--text-muted)] mt-1">Da 1 a 30 contenuti per questa campagna. Scegli il numero, senza imporre 7 pubblicazioni.</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.035)', border: '1px solid var(--border-color)' }}>
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase text-[var(--text-muted)]">Obiettivo</p>
                <p className="text-[11px] text-[var(--text-muted)] mt-1">Feeding = nurturing informativo dei follower già acquisiti. Internamente continua a usare il campo storico “education”.</p>
              </div>
              <div className="space-y-3">
                {objectiveRows.map(obj => <div key={obj.id}>
                  <div className="flex items-center justify-between text-sm mb-2"><span className="font-medium">{obj.label}</span><strong>{obj.value}%</strong></div>
                  <input type="range" min="0" max="100" value={obj.value} onChange={e => handleObjectiveChange(obj.id, e.target.value)} className="slider-dark w-full" data-testid={`quickstart-objective-${obj.id}`} />
                </div>)}
              </div>
            </div>

            <div className="rounded-xl p-3 text-xs" style={{ background: 'rgba(124,92,255,.08)', border: '1px solid rgba(124,92,255,.18)' }}>
              <p className="font-semibold mb-1">Setup cliente riutilizzato</p>
              {workspaceLoading ? <p className="text-[var(--text-muted)]">Verifico il setup del cliente…</p> :
                workspaceFailed ? <p className="text-rose-300">Impossibile verificare il setup: riprova a selezionare il cliente.</p> :
                <p className="text-[var(--text-muted)]">
                {hasV3Design ? `✓ Design V3 · ${v3TemplateCount} template` : hasSupportDesign ? '✓ Design base (V3 da recuperare)' : '— Design da recuperare'} · {hasTov ? '✓ Tone of Voice' : '— Tone of Voice da recuperare'}
              </p>}
              <p className="text-[var(--text-muted)] mt-1">Template V3, font, brand e Tone of Voice disponibili vengono ereditati automaticamente: non verranno richiesti di nuovo.</p>
            </div>
            <ClientWorkspaceRecovery api={api} clientId={selectedId} workspace={workspace} onRecovered={setWorkspace} />
          </>}
        </div>}

        <div className="flex justify-end gap-3 mt-6">
          <button className="btn-ghost" onClick={onClose}>Annulla</button>
          <button className="btn-gradient" onClick={create} disabled={!selected || !projectName.trim() || creating || !entitlementsResolved || workspaceLoading || workspaceFailed || !workspace} data-testid="quickstart-create-btn">
            {creating || !entitlementsResolved ? 'Preparazione…' : canFastGenerate ? (proEditorialEngine ? 'Crea e genera Master' : 'Crea e genera') : 'Crea e completa'} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
